// ====================================================================================
// MODAL DE SUPORTE - CRIAR TICKET
// ====================================================================================
// PROMPT BLINDADO: Componente reutilizável para cliente/prestador abrir ticket
// ====================================================================================

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  HelpCircle, Send, Loader2, X, CheckCircle2,
  CreditCard, User, Wrench, MessageSquare, MoreHorizontal
} from 'lucide-react';

const CATEGORIES = [
  { value: 'service', label: 'Serviço / Agendamento', icon: Wrench, color: 'text-blue-600' },
  { value: 'payment', label: 'Pagamento / Cobrança', icon: CreditCard, color: 'text-green-600' },
  { value: 'account', label: 'Minha Conta', icon: User, color: 'text-purple-600' },
  { value: 'technical', label: 'Problema Técnico', icon: MessageSquare, color: 'text-orange-600' },
  { value: 'other', label: 'Outro', icon: MoreHorizontal, color: 'text-slate-600' }
];

export default function SupportModal({ isOpen, onClose, onTicketCreated }) {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const resetForm = () => {
    setCategory('');
    setSubject('');
    setDescription('');
    setSuccess(false);
    setTicketId(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validações
    if (!category) {
      toast.error('Selecione uma categoria');
      return;
    }
    if (!subject || subject.trim().length < 5) {
      toast.error('O assunto deve ter pelo menos 5 caracteres');
      return;
    }
    if (!description || description.trim().length < 10) {
      toast.error('A descrição deve ter pelo menos 10 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/support/tickets`,
        {
          category,
          subject: subject.trim(),
          description: description.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTicketId(res.data.ticket_id);
      setSuccess(true);
      toast.success('Ticket criado com sucesso!');
      onTicketCreated?.();
    } catch (error) {
      console.error('Erro ao criar ticket:', error);
      const message = error.response?.data?.detail || 'Erro ao criar ticket';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            {success ? 'Ticket Criado!' : 'Abrir Ticket de Suporte'}
          </DialogTitle>
          <DialogDescription>
            {success 
              ? 'Sua solicitação foi recebida e será analisada pela nossa equipe.'
              : 'Descreva sua dúvida ou problema e nossa equipe responderá em breve.'
            }
          </DialogDescription>
        </DialogHeader>

        {success ? (
          // Tela de sucesso
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-lg font-semibold text-green-700 mb-2">
              Ticket #{ticketId?.slice(0, 8)}
            </p>
            <p className="text-sm text-slate-500">
              Você receberá uma resposta por aqui ou por e-mail.
            </p>
          </div>
        ) : (
          // Formulário
          <div className="space-y-4 py-4">
            {/* Categoria */}
            <div className="space-y-2">
              <Label htmlFor="category">Categoria *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category" data-testid="support-category-select">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${cat.color}`} />
                          {cat.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Assunto */}
            <div className="space-y-2">
              <Label htmlFor="subject">Assunto *</Label>
              <Input
                id="subject"
                placeholder="Ex: Problema com agendamento"
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, 100))}
                disabled={submitting}
                data-testid="support-subject-input"
              />
              <p className="text-xs text-slate-400 text-right">{subject.length}/100</p>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                placeholder="Descreva seu problema ou dúvida com detalhes..."
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
                disabled={submitting}
                rows={4}
                data-testid="support-description-input"
              />
              <p className="text-xs text-slate-400 text-right">{description.length}/1000</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {success ? (
            <Button onClick={handleClose} className="w-full" data-testid="support-close-btn">
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                data-testid="support-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
